import {IEndPoint, IO} from "lib/commons/io/io";
import {AuthSrv, JUserUpdate, OUserSrvBaseConfig, UserSrvBase} from 'lib/core/user';
import {CDM} from "lib/commons/utils/cdm";

/**
 * Server de login et de modification des propriétés de son compte.
 *
 */
export class UserSelfSrv extends UserSrvBase {

	protected auth?: AuthSrv;

	constructor(public readonly config: OUserSelfSrvConfig) {
		super();
	}

	/** Permet de notifier cet authServer d'un acte de login / logout réussit. */
	connectToAuth(auth: AuthSrv) {
		this.auth = auth;
	}

	async login(props: OLoginMainStepProps): Promise<UserSelfResponse<EUserLoginResult>> {
		if (!this.config.loginUrl) throw "Service unavailable";
		const resp = await sendUserSelfRequest<EUserLoginResult>(this.config.loginUrl, props);
		if (this.auth && resp.result === "logged") {
			await this.auth.fetchUser();
		}
		return resp;
	}

	async logout(): Promise<UserSelfResponse<EUserLogoutResult | 'canceled'>> {
		if (!this.config.logoutUrl) throw "Service unavailable";
		if (this.auth && await this.auth.listeners.emitAsyncUntil('beforeLogout', this.auth.currentUser) === 'stop') return {result: 'canceled'};
		const resp = await sendUserSelfRequest<EUserLogoutResult>(this.config.logoutUrl);
		if (this.auth && resp.result === "loggedOut") {
			this.auth.listeners.emit('afterLogout');
			this.auth.onDisconnect();
		}
		return resp;
	}

	async pwdLost(props: OIdentifUser): Promise<UserSelfResponse<EAskForUpdateUserResult>> {
		if (!this.config.pwdLostUrl) throw "Service unavailable";
		const url = new URL(this.config.pwdLostUrl.url);
		url.searchParams.append("param", props.nickOrAccount);
		return await sendUserSelfRequest<EAskForUpdateUserResult>(this.config.pwdLostUrl.resolve(url.href), null, true);
	}

	async pwdLostUpdate(props: OIdentifUser & OCredentialFields): Promise<UserSelfResponse<EUserUpdateResult>> {
		if (!this.config.pwdLostUrl) throw "Service unavailable";
		return await sendUserSelfRequest<EUserUpdateResult>(this.config.pwdLostUrl.resolve(IO.qs("cdaction", "UpdateUser")), props, true);
	}

	async renewPwd(props: OLoginMainStepProps & OCredentialFields): Promise<UserSelfResponse<EUserUpdateResult | EUserCheckFieldsResult>> {
		if (!this.config.renewPwdUrl) throw "Service unavailable";
		return await sendUserSelfRequest<EUserLoginResult & EUserCheckFieldsResult>(this.config.renewPwdUrl, props, true);
	}

	async checkPwd(props: OCredentialFields): Promise<UserSelfResponse<EUserCheckFieldsResult>> {
		if (!this.config.checkPwdUrl) throw "Service unavailable";
		return sendUserSelfRequest<EUserCheckFieldsResult>(this.config.checkPwdUrl, props);
	}

	/**
	 * Création d'un utilisateur
	 * @param props
	 */
	async createUser(props: JUserUpdate): Promise<UserSelfResponse<EUserCreateResult, EUserCreateError>> {
		if (!this.config.createUserUrl) throw "Service unavailable";
		const url = new URL(this.config.createUserUrl.url);
		url.searchParams.append("param", props.account);
		const r = await sendUserSelfRequest<EUserCreateResult, EUserCreateError>(this.config.createUserUrl.resolve(url.href), props, true);
		if (this.auth && props.account) this.auth.listeners.emit("userUpdated", props.account, props);
		return r;
	}

	/**
	 * Création d'un groupe
	 * @param props
	 */
	async createGroup(props: JUserUpdate): Promise<UserSelfResponse<EUserCreateResult, EUserCreateError>> {
		if (!this.config.createGroupUrl) throw "Service unavailable";
		const url = new URL(this.config.createGroupUrl.url);
		url.searchParams.append("param", props.account);
		const r = await sendUserSelfRequest<EUserCreateResult, EUserCreateError>(this.config.createGroupUrl.resolve(url.href), props, true);
		if (this.auth && props.account) this.auth.listeners.emit("userUpdated", props.account, props);
		return r;
	}

	/**
	 * Actualisation des propriétés d'un utilisateur (potentiellement autre que l'utilisateur courant)
	 * Utiliser user.updateCurrentUser pour modifier les propriétés de l'utilisateur connecté
	 * @param props
	 */
	async updateUser(props: JUserUpdate): Promise<UserSelfResponse<EUserUpdateResult, EUserUpdateError>> {
		if (!this.config.updateUserUrl) throw "Service unavailable";
		const r = await sendUserSelfRequest<EUserUpdateResult, EUserUpdateError>(this.config.updateUserUrl, props, true);
		const auth = this.auth;
		if (auth) {
			if (props.account === auth.currentUser?.account) await auth.fetchUser();
			auth.listeners.emit("userUpdated", props.account, props);
		}
		return r;
	}

	/**
	 * Actualisation des propriétés d'un groupe
	 * @param props
	 */
	async updateGroup(props: JUserUpdate): Promise<UserSelfResponse<EUserUpdateResult, EUserUpdateError>> {
		if (!this.config.updateGroupUrl) throw "Service unavailable";
		const r = await sendUserSelfRequest<EUserUpdateResult, EUserUpdateError>(this.config.updateGroupUrl, props, true);
		const auth = this.auth;
		if (auth) {
			await auth.fetchUser(); // utile si changement de groupe du user courant
			auth.listeners.emit("userUpdated", props.account, props);
		}
		return r;
	}
}

export interface OUserSelfSrvConfig extends OUserSrvBaseConfig {
	/** service de login (accès public). */
	loginUrl?: IEndPoint;

	/** service de logout (accès public). */
	logoutUrl?: IEndPoint;

	/** service de perte de mot de passe (accès public). */
	pwdLostUrl?: IEndPoint;

	/** service d'analyse de validité d'un nouveau mot de passe (accès public). */
	checkPwdUrl?: IEndPoint;

	/** service de changement de mot de passe (accès public). */
	renewPwdUrl?: IEndPoint;

	/** service de mise à jour d'un utilisateur (accès protégé). */
	updateUserUrl?: IEndPoint;

	/** service de mise à jour d'un groupe (accès protégé). */
	updateGroupUrl?: IEndPoint;

	/** service de creation d'un utilisateur (accès protégé). */
	createUserUrl?: IEndPoint;

	/** service de creation d'un groupe (accès protégé). */
	createGroupUrl?: IEndPoint;

}

export function configUserSelfSrv(publicExecFrameUrl: IEndPoint, authenticatedExecFrameUrl: IEndPoint, config?: OUserSelfSrvConfig): OUserSelfSrvConfig {
	if (!config) config = {} as OUserSelfSrvConfig;
	if (!("loginUrl" in config)) config.loginUrl = publicExecFrameUrl.resolve("u/loginWeb");
	if (!("logoutUrl" in config)) config.logoutUrl = authenticatedExecFrameUrl.resolve("u/logoutWeb");
	if (!("pwdLostUrl" in config)) config.pwdLostUrl = publicExecFrameUrl.resolve("u/pwdLost");
	if (!("checkPwdUrl" in config)) config.checkPwdUrl = publicExecFrameUrl.resolve("u/checkPwdWeb");
	if (!("renewPwdUrl" in config)) config.renewPwdUrl = publicExecFrameUrl.resolve("u/renewPwdWeb");
	if (!("updateUserUrl" in config)) config.updateUserUrl = authenticatedExecFrameUrl.resolve("u/updateUser");
	if (!("updateGroupUrl" in config)) config.updateGroupUrl = authenticatedExecFrameUrl.resolve("u/updateGroup");
	if (!("createUserUrl" in config)) config.createUserUrl = authenticatedExecFrameUrl.resolve("u/createUser");
	if (!("createGroupUrl" in config)) config.createGroupUrl = authenticatedExecFrameUrl.resolve("u/createGroup");
	return config;
}


type OIdentifUser = {
	nickOrAccount: string
	updateToken?: string
}

interface OCheckCurrentPasswordStepProps {
	currentPwd: string
}

interface OCredentialFields {
	password?: string
	//2 factor auth...
}

interface OCookiesProps {
	cookieMaxAge?: number;
}

export type OLoginMainStepProps = OIdentifUser & OCheckCurrentPasswordStepProps & OCookiesProps;

/** Retours standards pour l'auth d'un user : {@link SvcUserSelfMgrDialog#CDACTION_Login}. */
type EUserLoginResult =
/** L'auth a réussi. */
	'logged'

	/** Le compte est introuvable. */
	| 'accountNotFound'

	/** Le compte n'est pas actif. */
	| 'accountDisabled'

	/** Password (ou autre technique de validation du compte) invalide. */
	| 'invalidPassword'

	/** Password juste, mais périmé, obligation d'en saisir un nouveau. */
	| 'obsoletPassword'

	/** Autre Echec, système d'auth non disponible. */
	| 'otherFailure';

/** Retours standards pour le logout d'un user : {@link SvcUserSelfMgrDialog#CDACTION_Logout}. */
type EUserLogoutResult =
/** Ok. */
	'loggedOut'

	/** La session est introuvable. */
	| 'sessionNotFound'

	/** Autre Echec, système indisponible. */
	| 'otherFailure';

/** Retours standards pour l'auto-création d'un user  : {@link SvcUserSelfMgrDialog#CDACTION_Create} */
type EUserCreateResult =
/** Le compte a été créé et est dès à présent disponible. */
	'available'

	/** Le compte est en cous de création, en attente de validation. */
	| 'validationPending'

	/** Echec :  Le nom du compte existe déjà ou est en conflit avec un alias existant. */
	| 'failedAccountConflict'

	/** Echec :  des champs du user à créer sont invalides. */
	| 'failedInvalidDatas'

	/** Autre Echec. */
	| 'otherFailure';

/** Retours standards des erreurs pour l'auto-création d'un user  : {@link SvcUserSelfMgrDialog#CDACTION_Create} */
type EUserCreateError = JNickConflictError;

/**
 * Retours standards pour enclencher un process de modification
 * d'un user : {@link SvcUserSelfMgrDialog#CDACTION_UpdateUser}.
 */
type EAskForUpdateUserResult =
/** Les instructions ont été envoyées pour le renouvellement (mail envoyé, etc.). */
	'instructionsSent'

	/** Le compte est introuvable. */
	| 'accountNotFound'

	/** Action impossible par manque de données (pas d'adresse mail fourni, etc.). */
	| 'datasNotAvailable'

	/** Autre Echec. */
	| 'otherFailure';

/** Retours standards pour modifier un user : {@link SvcUserSelfMgrDialog#CDACTION_UpdateUser}.*/
type EUserUpdateResult =
/** */
	'updated'

	/** Token périmé (ou password à contrôler périmé), modification impossible. */
	| 'tooOldToken'

	/** Token invalide (ou password à contrôler invalide). */
	| 'invalidToken'

	/** User non trouvé. */
	| 'accountNotFound'

	/** Modif interdite sur un compte désactivé. */
	| 'accountDisabled'

	/** Echec : des champs du user à modifier sont invalides. */
	| 'failedInvalidDatas'

	/** Autre Echec. */
	| 'otherFailure';

/** Retours standards des erreurs pour modifier un user : {@link SvcUserSelfMgrDialog#CDACTION_UpdateUser}.*/
type EUserUpdateError = JNickConflictError | JUserGroupSwitchError;


/**
 * Retours standards pour le processus de validation serveur de certains champs
 * d'un user : {@link SvcUserSelfMgrDialog#CDACTION_Login}.
 */
type EUserCheckFieldsResult =
	'ok'

	/** Echec : au moins un des champs à valider est invalide. */
	| 'failedInvalidDatas'

	/**
	 * Si ce processus de validation des champs exige un contrôle de user existant ou un token valide :
	 * Token périmé (ou password à contrôler périmé), contôle impossible.
	 */
	| 'tooOldToken'

	/**
	 * Si ce processus de validation des champs exige un contrôle de user existant ou un token valide :
	 * Token invalide (ou password à contrôler invalide).
	 */
	| 'invalidToken'

	/**
	 * Si ce processus de validation des champs exige un contrôle de user existant ou un token valide :
	 * User non trouvé.
	 */
	| 'accountNotFound'

	/**
	 * Si ce processus de validation des champs exige un contrôle de user existant ou un token valide :
	 * Validation impossible sur un compte désactivé.
	 */
	| 'accountDisabled'

	/** Autre Echec. */
	| 'otherFailure';


/**
 * Erreurs UserSelfResponse.secondaryResults.error
 */
interface IUserSelfError {
	type: string
}

interface JNickConflictError extends IUserSelfError {
	type: "NickConflictException";
	nameInConflict: string;
}

interface JUserGroupSwitchError extends IUserSelfError {
	type: "UserGroupSwitchException";
}

export interface UserSelfResponse<R, E extends IUserSelfError = any> {
	result: R;
	secondaryResults?: {
		msgs?: string[],
		error?: E,
		disabledEndDt?: number,
		password?: string
	}
}

async function sendUserSelfRequest<R, E extends IUserSelfError = any>(url: IEndPoint, props?: Dict<any>, addCsrfHeader?: boolean): Promise<UserSelfResponse<R | 'otherFailure', E>> {
	const initReq = {method: 'POST'} as RequestInit;
	if (props) {
		const body = initReq.body = new FormData();
		body.append("userProps", CDM.stringify(props));
	}
	if (addCsrfHeader) initReq.headers = {"ScCsrf": "1"};
	try {
		const resp = await url.fetchJson<UserSelfResponse<R, E>>("", IO.addLang(initReq));
		return resp || {result: 'otherFailure'}; //XXX resp==null => 404 Créer IUserSelfNetworkError en secondaryResults ?
	} catch (e) {
		return {
			result: 'otherFailure',
			secondaryResults: {
				error: 'message' in e ? e.message : e //XXX à revoir ne respecte pas IUserSelfError. Créer IUserSelfClientError ?
			}
		};
	}
}

export namespace USERSELF {
	/**
	 * @return null si pas d'erreur, ou str si une erreur a été détectée
	 * @param usr
	 * FIXME : non complet
	 */
	export function tcheckUserSelfRespError(resp: UserSelfResponse<any, IUserSelfError>, defaultMsg?: string): string | null {
		let defaultErrorStr: string = defaultMsg != null ? defaultMsg : "erreur indéterminée";
		if (!resp)
			return defaultErrorStr;
		// Update
		let tcheck = tcheckUserSelfRespError_update(resp as UserSelfResponse<EUserUpdateResult, EUserUpdateError>);
		if (tcheck === null || tcheck.length > 0) return tcheck;
		// Create
		tcheck = tcheckUserSelfRespError_create(resp as UserSelfResponse<EUserCreateResult, EUserCreateError>);
		if (tcheck === null || tcheck.length > 0) return tcheck;
		// Default / commun
		switch (resp.result) {
		case 'failedInvalidDatas':
			if (resp.secondaryResults && resp.secondaryResults.error) {
				switch (resp.secondaryResults.error.type) {
				case "NickConflictException":
					let pseudo = (resp.secondaryResults.error as JNickConflictError).nameInConflict;
					return `Le pseudo '${pseudo}' a déjà été utilisé, veuillez en saisir un autre.`;
				case "UserGroupSwitchException":
					return "Le changement de type groupe/utilisateur n'est pas autorisé.";
				}
			}
			return "Informations fournies incorrectes."
		default :
			return defaultErrorStr;
		}
	}

	function tcheckUserSelfRespError_update(resp: UserSelfResponse<EUserUpdateResult, EUserUpdateError>): string | "" | null {
		switch (resp.result) {
		case 'updated' :
			return null;
		case 'tooOldToken':
		case 'invalidToken':
			return "Votre demande de changement de mot de passe a expiré."
		case 'accountNotFound':
			return "Ce compte n'existe pas."
		case 'accountDisabled':
			const ts = resp.secondaryResults ? resp.secondaryResults.disabledEndDt : 0;
			if (ts > (Date.now() - 5000)) {
				const lang = document.documentElement.lang || undefined;
				const date = new Date(ts);
				const localeDate = date.toLocaleDateString(lang);
				const localeTime = date.toLocaleTimeString(lang, {hour: "numeric", minute: "numeric"});
				return `Ce compte a été temporairement désactivé et sera de nouveau actif le ${localeDate} vers ${localeTime}.`;
			} else
				return "Ce compte est désactivé."
		case 'failedInvalidDatas':
		case 'otherFailure' :
		default :
			return "";
		}
	}

	function tcheckUserSelfRespError_create(resp: UserSelfResponse<EUserCreateResult, EUserCreateError>): string | "" | null {
		switch (resp.result) {
		case 'available' :
			return null;
		case 'validationPending':
			return null
		case 'failedAccountConflict':
			return "Cet identifiant de compte a déjà été utilisé."
		case 'failedInvalidDatas':
		default :
			return "";
		}
	}

}